import React, { useEffect, useState } from 'react';
import API from '../api/axios';
import Header from '../components/Header';
import { Search, ArrowUpRight, ArrowDownLeft, RefreshCw, Wallet } from 'lucide-react';

const TYPE_CONFIG = {
  payment: { icon: ArrowUpRight, color: 'text-green-500', bg: 'bg-green-50', label: 'Payment' },
  refund: { icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Refund' },
  wallet_topup: { icon: ArrowDownLeft, color: 'text-purple-500', bg: 'bg-purple-50', label: 'Wallet Top-up' },
  wallet_debit: { icon: ArrowUpRight, color: 'text-orange-500', bg: 'bg-orange-50', label: 'Wallet Debit' },
};

const STATUS_CONFIG = {
  success: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-600',
};

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    API.get('/payments/transactions/').then(r => setTransactions(r.data)).finally(() => setLoading(false));
  }, []);

  const totalRevenue = transactions
    .filter(t => t.status === 'success' && t.transaction_type === 'payment')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);

  const filtered = transactions.filter(t =>
    (filter === 'all' || t.transaction_type === filter) &&
    (!search || t.razorpay_payment_id?.toLowerCase().includes(search.toLowerCase()) || t.razorpay_order_id?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="flex-1 overflow-auto">
      <Header title="Transactions" subtitle={`${transactions.length} total transactions`} />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Revenue', value: `₹${totalRevenue.toLocaleString('en-IN')}`, color: 'from-green-500 to-green-600', icon: ArrowUpRight },
            { label: 'Successful', value: transactions.filter(t => t.status === 'success').length, color: 'from-blue-500 to-blue-600', icon: RefreshCw },
            { label: 'Wallet Top-ups', value: transactions.filter(t => t.transaction_type === 'wallet_topup').length, color: 'from-purple-500 to-purple-600', icon: Wallet },
            { label: 'Failed', value: transactions.filter(t => t.status === 'failed').length, color: 'from-red-500 to-red-600', icon: ArrowDownLeft },
          ].map(({ label, value, color, icon: Icon }) => (
            <div key={label} className="card">
              <div className={`w-9 h-9 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center mb-3 shadow-md`}>
                <Icon size={16} className="text-white" />
              </div>
              <p className="text-xs text-gray-500 mb-0.5">{label}</p>
              <p className="text-xl font-black text-gray-900">{value}</p>
            </div>
          ))}
        </div>

        <div className="card flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search by payment ID or order ID..." className="input pl-9"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'payment', 'refund', 'wallet_topup', 'wallet_debit'].map(t => (
              <button key={t} onClick={() => setFilter(t)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${filter === t ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-gray-200 text-gray-600 hover:border-orange-200'}`}>
                {t === 'all' ? 'All' : TYPE_CONFIG[t]?.label || t}
              </button>
            ))}
          </div>
        </div>

        <div className="card overflow-x-auto p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="table-header">ID</th>
                <th className="table-header">Type</th>
                <th className="table-header">Amount</th>
                <th className="table-header">Status</th>
                <th className="table-header">Order ID</th>
                <th className="table-header">Payment ID</th>
                <th className="table-header">Description</th>
                <th className="table-header">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-gray-50">
                  {[...Array(8)].map((_, j) => <td key={j} className="table-cell"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}
                </tr>
              )) : filtered.map(t => {
                const tc = TYPE_CONFIG[t.transaction_type] || TYPE_CONFIG.payment;
                const Icon = tc.icon;
                return (
                  <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="table-cell font-mono text-xs text-gray-400">#{t.id}</td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 ${tc.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon size={13} className={tc.color} />
                        </div>
                        <span className="text-xs font-medium text-gray-700">{tc.label}</span>
                      </div>
                    </td>
                    <td className="table-cell font-black text-gray-900">₹{parseFloat(t.amount).toLocaleString('en-IN')}</td>
                    <td className="table-cell"><span className={`badge ${STATUS_CONFIG[t.status]}`}>{t.status}</span></td>
                    <td className="table-cell"><span className="font-mono text-xs text-gray-500 max-w-28 truncate block">{t.razorpay_order_id || '—'}</span></td>
                    <td className="table-cell"><span className="font-mono text-xs text-gray-500 max-w-28 truncate block">{t.razorpay_payment_id || '—'}</span></td>
                    <td className="table-cell text-xs text-gray-500 max-w-40 truncate">{t.description}</td>
                    <td className="table-cell text-xs text-gray-500">{new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <div className="text-4xl mb-3">💳</div>
              <p>No transactions found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
